import { useEffect, useRef, type RefObject } from "react";

const NAME = "dw-find";

interface HighlightRegistry {
  set(key: string, value: unknown): void;
  delete(key: string): void;
}
type HighlightCtor = new (...ranges: Range[]) => unknown;

/**
 * Highlight every case-insensitive occurrence of `query` within `ref`'s
 * rendered text, using the **CSS Custom Highlight API** (`::highlight(dw-find)`).
 * It paints `Range`s without mutating the DOM, so it never fights React — unlike
 * wrapping text nodes in `<mark>`. No-op where unsupported or `query` is short.
 * The first match is scrolled into view once per query. `dep` (the rendered
 * source) forces a recompute when content changes; re-highlighting on the same
 * query doesn't re-scroll.
 */
export function useFindHighlight(
  ref: RefObject<HTMLElement | null>,
  query: string,
  dep?: unknown,
): void {
  const scrolledFor = useRef<string | null>(null);
  useEffect(() => {
    const reg = (CSS as unknown as { highlights?: HighlightRegistry }).highlights;
    const Ctor = (window as unknown as { Highlight?: HighlightCtor }).Highlight;
    const el = ref.current;
    const q = query.trim().toLowerCase();
    if (!reg || !Ctor || !el || q.length < 2) {
      reg?.delete(NAME);
      scrolledFor.current = null;
      return;
    }

    const ranges: Range[] = [];
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let node: Node | null;
    while ((node = walker.nextNode())) {
      const text = (node.nodeValue ?? "").toLowerCase();
      let i = text.indexOf(q);
      while (i >= 0) {
        const r = document.createRange();
        r.setStart(node, i);
        r.setEnd(node, i + q.length);
        ranges.push(r);
        i = text.indexOf(q, i + q.length);
      }
    }

    if (ranges.length === 0) {
      reg.delete(NAME);
      return;
    }
    reg.set(NAME, new Ctor(...ranges));

    // Scroll to the first hit once per query (not on every content recompute).
    if (scrolledFor.current !== q) {
      scrolledFor.current = q;
      ranges[0].startContainer.parentElement?.scrollIntoView({
        block: "center",
      });
    }

    return () => reg.delete(NAME);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, dep]);
}
